import { useState, useEffect, useCallback, type ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { ArrowRight, Phone, Mail, Printer, Truck, Loader2, Package, MapPin, FileText, Clock } from "lucide-react";

interface OrderItem {
  readonly id: string;
  readonly productName: string;
  readonly variantName: string | null;
  readonly sku: string | null;
  readonly unitPrice: number;
  readonly quantity: number;
  readonly lineTotal: number;
  readonly imageUrl: string | null;
}

interface StatusHistoryEntry {
  readonly id: string;
  readonly fromStatus: string | null;
  readonly toStatus: string;
  readonly changedBy: string | null;
  readonly note: string | null;
  readonly createdAt: string;
}

interface OrderDetail {
  readonly id: string;
  readonly orderNumber: number;
  readonly status: string;
  readonly customerName: string;
  readonly customerPhone: string;
  readonly customerEmail: string | null;
  readonly shippingMethod: string | null;
  readonly shippingAddress: { line1: string; line2?: string; city: string; zip: string } | null;
  readonly customerNote: string | null;
  readonly subtotal: number;
  readonly discount: number;
  readonly shippingCost: number;
  readonly total: number;
  readonly vatAmount: number;
  readonly couponCode: string | null;
  readonly createdAt: string;
  readonly items: readonly OrderItem[];
  readonly statusHistory: readonly StatusHistoryEntry[];
}

const STATUS_MAP: Record<string, { label: string; className: string }> = {
  pending_payment: { label: "ממתין לתשלום", className: "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/30" },
  paid:            { label: "שולם",          className: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30" },
  processing:      { label: "בטיפול",        className: "bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/30" },
  shipped:         { label: "נשלח",          className: "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border-cyan-500/30" },
  delivered:       { label: "נמסר",          className: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" },
  cancelled:       { label: "בוטל",          className: "bg-destructive/15 text-destructive border-destructive/30" },
  refunded:        { label: "הוחזר",         className: "bg-orange-500/15 text-orange-600 dark:text-orange-400 border-orange-500/30" },
};

const NEXT_STATUSES: Record<string, readonly string[]> = {
  pending_payment: ["paid", "cancelled"],
  paid: ["processing", "cancelled", "refunded"],
  processing: ["shipped", "cancelled", "refunded"],
  shipped: ["delivered", "refunded"],
  delivered: [],
  cancelled: [],
  refunded: [],
};

function formatNIS(agorot: number): string {
  return `₪${(agorot / 100).toFixed(2)}`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("he-IL", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function StatusPill({ status }: { readonly status: string }): ReactNode {
  const s = STATUS_MAP[status] ?? STATUS_MAP["pending_payment"]!;
  return (
    <span className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold", s.className)}>
      {s.label}
    </span>
  );
}

export function OrderDetailPage(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [showConfirm, setShowConfirm] = useState<string | null>(null);

  const fetchOrder = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/orders/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const json = (await res.json()) as { success: boolean; data: OrderDetail };
      if (json.success) {
        setOrder(json.data);
        setInternalNote(json.data.customerNote ?? "");
      }
    } finally {
      setLoading(false);
    }
  }, [id, accessToken]);

  useEffect(() => { void fetchOrder(); }, [fetchOrder]);

  const updateStatus = useCallback(async (newStatus: string) => {
    if (!id || updating) return;
    if (newStatus === "shipped" && !trackingNumber.trim()) return;
    setUpdating(true);
    try {
      const res = await fetch(`/api/orders/${id}/status`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          status: newStatus,
          note: statusNote || undefined,
          trackingNumber: newStatus === "shipped" ? trackingNumber : undefined,
        }),
      });
      const json = (await res.json()) as { success: boolean };
      if (json.success) {
        setShowConfirm(null);
        setStatusNote("");
        setTrackingNumber("");
        void fetchOrder();
      }
    } finally {
      setUpdating(false);
    }
  }, [id, accessToken, trackingNumber, statusNote, updating, fetchOrder]);

  const saveNote = useCallback(async () => {
    if (!id) return;
    await fetch(`/api/orders/${id}/note`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({ internalNote }),
    });
  }, [id, accessToken, internalNote]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex flex-col items-center py-16 gap-4">
        <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
          <Package className="h-8 w-8 text-muted-foreground" />
        </div>
        <p className="text-muted-foreground">הזמנה לא נמצאה</p>
        <Button variant="outline" onClick={() => navigate("/orders")}>
          חזרה להזמנות
        </Button>
      </div>
    );
  }

  const nextStatuses = NEXT_STATUSES[order.status] ?? [];

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/orders")} className="min-h-[38px]">
          <ArrowRight className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold font-mono">#{order.orderNumber}</h1>
            <StatusPill status={order.status} />
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">{formatDateTime(order.createdAt)}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => window.print()} className="min-h-[38px]">
          <Printer className="h-4 w-4 me-1.5" />
          הדפסה
        </Button>
      </div>

      {/* Status workflow */}
      {nextStatuses.length > 0 && (
        <Card className="shadow-sm border-primary/20 bg-primary/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">עדכון סטטוס</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {showConfirm === "shipped" && (
              <div>
                <label className="text-sm font-medium">מספר מעקב משלוח</label>
                <Input
                  placeholder="IL123456789"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  className="mt-1.5 min-h-[42px]"
                  dir="ltr"
                />
              </div>
            )}
            {showConfirm && (
              <div className="space-y-2">
                <Input
                  placeholder="הערה (אופציונלי)"
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  className="min-h-[42px]"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={updating || (showConfirm === "shipped" && !trackingNumber.trim())}
                    onClick={() => void updateStatus(showConfirm)}
                    className="min-h-[44px]"
                  >
                    {updating && <Loader2 className="h-3.5 w-3.5 animate-spin me-1" />}
                    אישור
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowConfirm(null)} className="min-h-[44px]">
                    ביטול
                  </Button>
                </div>
              </div>
            )}
            {!showConfirm && (
              <div className="flex flex-wrap gap-2">
                {nextStatuses.map((ns) => {
                  const isDestructive = ns === "cancelled" || ns === "refunded";
                  return (
                    <Button
                      key={ns}
                      size="sm"
                      variant={isDestructive ? "destructive" : "default"}
                      onClick={() => setShowConfirm(ns)}
                      className="min-h-[44px]"
                    >
                      {STATUS_MAP[ns]?.label ?? ns}
                    </Button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-3 gap-5">
        {/* Main column — items + totals */}
        <div className="md:col-span-2 space-y-5">
          {/* Items */}
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                פריטים ({order.items.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {order.items.map((item) => (
                <div key={item.id} className="flex items-center gap-3">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.productName} className="w-12 h-12 rounded-lg object-cover ring-1 ring-border shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-muted shrink-0 flex items-center justify-center">
                      <Package className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.productName}</p>
                    {item.variantName && <p className="text-xs text-muted-foreground">{item.variantName}</p>}
                    {item.sku && <p className="text-xs text-muted-foreground font-mono">SKU: {item.sku}</p>}
                  </div>
                  <div className="text-end shrink-0">
                    <p className="text-xs text-muted-foreground">{item.quantity} × {formatNIS(item.unitPrice)}</p>
                    <p className="text-sm font-semibold">{formatNIS(item.lineTotal)}</p>
                  </div>
                </div>
              ))}

              <Separator />

              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>סכום ביניים</span>
                  <span>{formatNIS(order.subtotal)}</span>
                </div>
                {order.discount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      הנחה{order.couponCode ? ` (${order.couponCode})` : ""}
                    </span>
                    <span className="text-emerald-600 font-medium">-{formatNIS(order.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-muted-foreground">
                  <span>משלוח</span>
                  <span>{order.shippingCost === 0 ? "חינם" : formatNIS(order.shippingCost)}</span>
                </div>
                <Separator />
                <div className="flex justify-between font-bold text-base">
                  <span>סה"כ</span>
                  <span>{formatNIS(order.total)}</span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>כולל מע"מ</span>
                  <span>{formatNIS(order.vatAmount)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Internal note */}
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                הערה פנימית
              </CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                className="w-full rounded-lg border bg-background px-3 py-2 text-sm min-h-[80px] resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
                onBlur={() => void saveNote()}
                placeholder="הוסף הערה פנימית..."
              />
            </CardContent>
          </Card>
        </div>

        {/* Side column — customer + shipping + timeline */}
        <div className="space-y-5">
          {/* Customer */}
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">לקוח</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="font-semibold">{order.customerName}</p>
              <a
                href={`tel:${order.customerPhone}`}
                className="flex items-center gap-2 text-sm text-primary hover:underline min-h-[44px]"
              >
                <Phone className="h-4 w-4 shrink-0" />
                {order.customerPhone}
              </a>
              {order.customerEmail && (
                <a
                  href={`mailto:${order.customerEmail}`}
                  className="flex items-center gap-2 text-sm text-primary hover:underline min-h-[44px] truncate"
                >
                  <Mail className="h-4 w-4 shrink-0" />
                  {order.customerEmail}
                </a>
              )}
              {order.customerNote && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 text-sm">
                  <p className="text-xs font-medium text-yellow-700 dark:text-yellow-400 mb-1">הערת לקוח</p>
                  {order.customerNote}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Shipping address */}
          {order.shippingAddress && (
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Truck className="h-4 w-4 text-muted-foreground" />
                  משלוח
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                  <div>
                    <p>{order.shippingAddress.line1}</p>
                    {order.shippingAddress.line2 && <p>{order.shippingAddress.line2}</p>}
                    <p>{order.shippingAddress.city}, {order.shippingAddress.zip}</p>
                  </div>
                </div>
                {order.shippingMethod && (
                  <p className="text-xs text-muted-foreground border rounded-md px-2.5 py-1.5 bg-muted/30 inline-block">
                    {order.shippingMethod}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Status timeline */}
          {order.statusHistory.length > 0 && (
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  היסטוריית סטטוס
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative space-y-0">
                  {order.statusHistory.map((h, idx) => {
                    const to = STATUS_MAP[h.toStatus];
                    const isLast = idx === order.statusHistory.length - 1;
                    return (
                      <div key={h.id} className="flex items-start gap-3 pb-4 relative">
                        {/* Timeline line */}
                        {!isLast && (
                          <div className="absolute start-[7px] top-4 bottom-0 w-px bg-border" />
                        )}
                        {/* Dot */}
                        <div className={cn(
                          "w-3.5 h-3.5 rounded-full shrink-0 mt-0.5 ring-2 ring-background",
                          idx === 0 ? "bg-primary" : "bg-muted-foreground/40"
                        )} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {h.fromStatus && (
                              <>
                                <span className="text-xs text-muted-foreground">{STATUS_MAP[h.fromStatus]?.label ?? h.fromStatus}</span>
                                <span className="text-xs text-muted-foreground">→</span>
                              </>
                            )}
                            <span className={cn(
                              "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold",
                              to?.className ?? "bg-secondary text-secondary-foreground border-transparent"
                            )}>
                              {to?.label ?? h.toStatus}
                            </span>
                          </div>
                          {h.note && <p className="text-xs text-muted-foreground mt-0.5">{h.note}</p>}
                          <p className="text-xs text-muted-foreground mt-0.5">{formatDateTime(h.createdAt)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
